import { inject } from "@angular/core";
import { CanActivateFn, Router } from "@angular/router";
import { CompanyContextService } from "@skylab-shared/core";
import { AuthCompanyContextRequestData, AuthGetCompanyContextResponse, AuthService, AuthUnauthorizedResponse } from "./auth.service";

const companyIdentityRegexp = /\/t\/(\d+)\//;
type AuthNavigationExtras = {
	redirectFromSuccessfullAuth: boolean;
}

export const authGuard = (): CanActivateFn => {
	return async () => {
		const authService = inject(AuthService);
		const companyContextService = inject(CompanyContextService);
		const router = inject(Router);

		const currentNav = router.currentNavigation();
		if ((currentNav?.extras?.info as AuthNavigationExtras)?.redirectFromSuccessfullAuth) {
			return true;
		}

		const params = new URLSearchParams(window.location.search);
		const contextId = params.get('contextId');
		const state = params.get('state');
		const code = params.get('code');

		const urlCompanyIdentityId = window.location.href.match(companyIdentityRegexp)?.[1];

		const handleRedirect = () => {
			const correctId = companyContextService.companyContext.companyIdentityId.toString();

			if (urlCompanyIdentityId === correctId.toString()) return true;
			router.navigate([`/t/${correctId}`], { info: { redirectFromSuccessfullAuth: true } });
			return false;
		}

		const handleCompanyContext = async (): Promise<boolean> => {
			const contextRequest: AuthCompanyContextRequestData = contextId ? { contextId } : { companyIdentityId: urlCompanyIdentityId };
			const companyContextResponse = await authService.getCompanyContext(contextRequest);

			if ((companyContextResponse as AuthUnauthorizedResponse).unauthorized) {
				return false;
			} else {
				const {companyIdentityId, tenantCorrelationId} = companyContextResponse as AuthGetCompanyContextResponse;
				companyContextService.companyContext = {companyIdentityId, tenantCorrelationId};
				return handleRedirect();
			}
		}

		if (state && code) {
			const response = await authService.provideTokenAsync({state, code, contextId: contextId!});
			if ((response as AuthUnauthorizedResponse).unauthorized) {
				return false;
			}

			const result = await handleCompanyContext();
			return result;
		} else {
			const response = await authService.getAccountInfoAsync();
			if ((response as AuthUnauthorizedResponse).unauthorized) {
				authService.redirectToLoginPage();
				return false;
			}

			return await handleCompanyContext();
		}
	};
}